import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import signingService from '../../services/signing.service';
import type { SigningSessionData, SigningField, SignatureMethod } from '../../types/esign';
import ESignConsentModal from './components/ESignConsentModal';
import SignatureCapture from './components/SignatureCapture';
import DeclineModal from './components/DeclineModal';
import SigningComplete from './components/SigningComplete';

// Configure pdfjs worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.mjs',
  import.meta.url
).toString();

export default function SigningCeremonyPage() {
  const { token } = useParams<{ token: string }>();
  const [session, setSession] = useState<SigningSessionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showConsent, setShowConsent] = useState(false);
  const [showSignature, setShowSignature] = useState<SigningField | null>(null);
  const [showDecline, setShowDecline] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [completionMessage, setCompletionMessage] = useState('');
  const [declined, setDeclined] = useState(false);

  // PDF rendering state
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pageHeight, setPageHeight] = useState(800);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Page view tracking
  const pageViewTimers = useRef<Map<number, number>>(new Map());
  const [viewedPages, setViewedPages] = useState<Set<number>>(new Set());

  // Load session
  useEffect(() => {
    if (!token) return;
    loadSession();
  }, [token]);

  const loadSession = async () => {
    try {
      setLoading(true);
      const data = await signingService.initializeSession(token!);
      setSession(data);

      // Show consent modal if not yet given
      if (data.consent.required && !data.consent.alreadyGiven) {
        setShowConsent(true);
      }

      // Load the actual PDF document
      loadPdf();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load signing session');
    } finally {
      setLoading(false);
    }
  };

  // Load PDF document
  const loadPdf = async () => {
    if (!token) return;
    try {
      setPdfLoading(true);
      const pdfUrl = await signingService.getDocumentUrl(token);
      const doc = await pdfjsLib.getDocument(pdfUrl).promise;
      setPdfDoc(doc);
    } catch (err) {
      console.error('Failed to load PDF:', err);
      // Don't block the ceremony - fields still work without PDF preview
    } finally {
      setPdfLoading(false);
    }
  };

  // Render current PDF page to canvas
  useEffect(() => {
    if (!pdfDoc || !canvasRef.current || !containerRef.current) return;

    let cancelled = false;

    const renderPage = async () => {
      try {
        const page = await pdfDoc.getPage(currentPage);
        if (cancelled) return;

        const containerWidth = containerRef.current?.clientWidth || 612;
        const viewport = page.getViewport({ scale: 1 });
        const scale = Math.min((containerWidth - 32) / viewport.width, 2); // cap at 2x
        const scaledViewport = page.getViewport({ scale });

        const canvas = canvasRef.current!;
        canvas.height = scaledViewport.height;
        canvas.width = scaledViewport.width;
        setPageHeight(scaledViewport.height);

        const context = canvas.getContext('2d')!;
        await page.render({
          canvasContext: context,
          viewport: scaledViewport,
        }).promise;
      } catch (err) {
        console.error('Failed to render page:', err);
      }
    };

    renderPage();

    return () => {
      cancelled = true;
    };
  }, [pdfDoc, currentPage]);

  // Track page view duration and record viewed pages
  useEffect(() => {
    if (!session || !token) return;

    const startTime = Date.now();
    pageViewTimers.current.set(currentPage, startTime);

    // Mark this page as viewed
    setViewedPages((prev) => {
      const next = new Set(prev);
      next.add(currentPage);
      return next;
    });

    return () => {
      const start = pageViewTimers.current.get(currentPage);
      if (start) {
        const duration = Math.round((Date.now() - start) / 1000);
        signingService.recordPageView(token, currentPage, duration).catch(() => {});
      }
    };
  }, [currentPage, session, token]);

  // Handle consent
  const handleConsentAccepted = useCallback(async () => {
    if (!token || !session) return;
    try {
      await signingService.acceptConsent(token, session.consent.version);
      setShowConsent(false);
      setSession((prev) => prev ? {
        ...prev,
        consent: { ...prev.consent, alreadyGiven: true },
      } : null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to record consent');
    }
  }, [token, session]);

  // Handle field value submission
  const handleFieldSubmit = useCallback(async (field: SigningField, value: string) => {
    if (!token) return;
    try {
      await signingService.submitFieldValue(token, field.id, value);
      setSession((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          fields: prev.fields.map((f) =>
            f.id === field.id ? { ...f, value, isCompleted: true } : f
          ),
          progress: {
            ...prev.progress,
            fieldsCompleted: prev.progress.fieldsCompleted + (field.isCompleted ? 0 : 1),
          },
        };
      });
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save field value');
    }
  }, [token]);

  // Handle signature submission
  const handleSignatureSubmit = useCallback(async (field: SigningField, data: string, method: SignatureMethod) => {
    if (!token) return;
    try {
      await signingService.submitSignature(token, field.id, data, method);
      setShowSignature(null);
      setSession((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          fields: prev.fields.map((f) =>
            f.id === field.id ? { ...f, value: data, isCompleted: true } : f
          ),
          progress: {
            ...prev.progress,
            fieldsCompleted: prev.progress.fieldsCompleted + (field.isCompleted ? 0 : 1),
          },
        };
      });
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save signature');
    }
  }, [token]);

  // Handle complete
  const handleComplete = useCallback(async () => {
    if (!token) return;
    try {
      setCompleting(true);
      const result = await signingService.completeSigning(token);
      setCompleted(true);
      setCompletionMessage(result.message);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to complete signing');
    } finally {
      setCompleting(false);
    }
  }, [token]);

  // Handle decline
  const handleDecline = useCallback(async (reason: string) => {
    if (!token) return;
    try {
      await signingService.declineSigning(token, reason);
      setShowDecline(false);
      setDeclined(true);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to decline');
    }
  }, [token]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto" />
          <p className="mt-4 text-gray-600">Loading document...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm border p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Unable to Load Document</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // Completed state
  if (completed) {
    return <SigningComplete message={completionMessage} />;
  }

  // Declined state
  if (declined) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm border p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Signing Declined</h2>
          <p className="text-gray-600">You have declined to sign this document. The sender has been notified.</p>
        </div>
      </div>
    );
  }

  if (!session) return null;

  const currentPageFields = session.fields.filter((f) => f.pageNumber === currentPage);
  const allRequiredDone = session.fields.filter((f) => f.isRequired).every((f) => f.isCompleted);
  const allPagesViewed = viewedPages.size >= session.envelope.pageCount;
  const canComplete = session.consent.alreadyGiven && allRequiredDone && allPagesViewed;

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b px-4 py-3 flex items-center justify-between shadow-sm">
        <div>
          <h1 className="text-sm font-semibold text-gray-900">{session.envelope.subject}</h1>
          <p className="text-xs text-gray-500">{session.envelope.documentName}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">
            Signing as <span className="font-medium text-gray-700">{session.signer.name}</span>
          </span>
          <button
            onClick={() => setShowDecline(true)}
            className="text-xs text-red-600 hover:text-red-700 font-medium"
          >
            Decline
          </button>
        </div>
      </header>

      {/* Progress bar */}
      <div className="bg-white border-b px-4 py-2">
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span>Page {currentPage} of {session.envelope.pageCount}</span>
          <span>|</span>
          <span>{session.progress.fieldsCompleted} of {session.progress.totalRequiredFields} fields completed</span>
          {!allPagesViewed && (
            <>
              <span>|</span>
              <span className="text-amber-600">
                {session.envelope.pageCount - viewedPages.size} page{session.envelope.pageCount - viewedPages.size !== 1 ? 's' : ''} remaining to view
              </span>
            </>
          )}
        </div>
        <div className="mt-1 h-1 bg-gray-200 rounded-full">
          <div
            className="h-1 bg-green-500 rounded-full transition-all"
            style={{
              width: `${session.progress.totalRequiredFields > 0
                ? (session.progress.fieldsCompleted / session.progress.totalRequiredFields) * 100
                : 0}%`,
            }}
          />
        </div>
      </div>

      {/* Document area with PDF and field overlays */}
      <div className="flex-1 overflow-auto p-4" ref={containerRef}>
        <div className="max-w-3xl mx-auto bg-white shadow rounded-lg relative" style={{ minHeight: pdfDoc ? pageHeight : 800 }}>
          {/* PDF canvas or loading/placeholder */}
          {pdfLoading ? (
            <div className="flex items-center justify-center" style={{ minHeight: 800 }}>
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mx-auto" />
                <p className="mt-2 text-sm text-gray-400">Loading document...</p>
              </div>
            </div>
          ) : pdfDoc ? (
            <canvas ref={canvasRef} className="w-full" />
          ) : (
            <div className="p-8 text-center text-gray-400 border-b" style={{ minHeight: 800 }}>
              <p className="text-sm">Document Page {currentPage}</p>
              <p className="text-xs mt-1">{session.envelope.documentName}</p>
              <p className="text-xs mt-2 text-amber-500">Unable to load PDF preview. Fields are still functional below.</p>
            </div>
          )}

          {/* Field overlays for current page */}
          {currentPageFields.map((field) => (
            <div
              key={field.id}
              className="absolute border-2 border-dashed rounded cursor-pointer transition-colors"
              style={{
                left: `${field.xPercent}%`,
                top: `${field.yPercent}%`,
                width: `${field.widthPercent}%`,
                height: `${field.heightPercent}%`,
                borderColor: field.isCompleted ? '#22c55e' : '#3b82f6',
                backgroundColor: field.isCompleted ? 'rgba(34, 197, 94, 0.05)' : 'rgba(59, 130, 246, 0.05)',
              }}
              onClick={() => {
                if (field.fieldType === 'SIGNATURE' || field.fieldType === 'INITIALS') {
                  setShowSignature(field);
                }
              }}
            >
              {!field.isCompleted && (
                <div className="absolute -top-5 left-0 text-xs font-medium text-blue-600 whitespace-nowrap">
                  {field.label}
                </div>
              )}

              {/* Checkbox field */}
              {field.fieldType === 'CHECKBOX' && !field.isCompleted && (
                <label className="w-full h-full flex items-center justify-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={field.value === 'true'}
                    onChange={(e) => {
                      handleFieldSubmit(field, String(e.target.checked));
                    }}
                    className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </label>
              )}

              {/* Text input for non-signature, non-checkbox fields */}
              {!field.isCompleted && field.fieldType !== 'SIGNATURE' && field.fieldType !== 'INITIALS' && field.fieldType !== 'CHECKBOX' && (
                <input
                  type="text"
                  placeholder={field.placeholder || field.label}
                  className="w-full h-full px-2 text-sm bg-transparent outline-none"
                  defaultValue={field.value || ''}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val && val !== field.value) {
                      handleFieldSubmit(field, val);
                    }
                  }}
                />
              )}

              {/* Show completed value for text-like fields */}
              {field.isCompleted && field.fieldType !== 'SIGNATURE' && field.fieldType !== 'INITIALS' && field.fieldType !== 'CHECKBOX' && (
                <div className="w-full h-full px-2 flex items-center text-sm text-gray-900">
                  {field.value}
                </div>
              )}

              {/* Show completed checkbox */}
              {field.isCompleted && field.fieldType === 'CHECKBOX' && (
                <div className="w-full h-full flex items-center justify-center">
                  <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                </div>
              )}

              {/* Show signature thumbnail */}
              {field.isCompleted && (field.fieldType === 'SIGNATURE' || field.fieldType === 'INITIALS') && field.value && (
                <img src={field.value} alt="Signature" className="w-full h-full object-contain p-1" />
              )}

              {/* Click to sign prompt */}
              {!field.isCompleted && (field.fieldType === 'SIGNATURE' || field.fieldType === 'INITIALS') && (
                <div className="w-full h-full flex items-center justify-center text-blue-600 text-xs font-medium">
                  Click to {field.fieldType === 'SIGNATURE' ? 'sign' : 'initial'}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Footer navigation */}
      <footer className="bg-white border-t px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          disabled={currentPage <= 1}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
        >
          Previous
        </button>

        <div className="flex items-center gap-3">
          {currentPage < session.envelope.pageCount ? (
            <button
              onClick={() => setCurrentPage((p) => Math.min(session.envelope.pageCount, p + 1))}
              className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded hover:bg-gray-800"
            >
              Next Page
            </button>
          ) : (
            <div className="flex items-center gap-2">
              {!canComplete && allRequiredDone && !allPagesViewed && (
                <span className="text-xs text-amber-600">Please view all pages before completing</span>
              )}
              <button
                onClick={handleComplete}
                disabled={!canComplete || completing}
                className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {completing ? 'Completing...' : 'Complete Signing'}
              </button>
            </div>
          )}
        </div>
      </footer>

      {/* Modals */}
      {showConsent && (
        <ESignConsentModal
          consentText={session.consent.text}
          signerName={session.signer.name}
          onAccept={handleConsentAccepted}
          onDecline={() => setShowDecline(true)}
        />
      )}

      {showSignature && (
        <SignatureCapture
          field={showSignature}
          onSubmit={(data, method) => handleSignatureSubmit(showSignature, data, method)}
          onClose={() => setShowSignature(null)}
        />
      )}

      {showDecline && (
        <DeclineModal
          onDecline={handleDecline}
          onCancel={() => setShowDecline(false)}
        />
      )}
    </div>
  );
}
